/**
 * PlanAccessGuard — Phase 12.30.
 *
 * Guarda de acesso por plano contratado + status do workspace.
 *
 * Camada A · Status do workspace:
 *   - Se `workspaceStatus ∈ {suspenso, cancelado}` → renderiza overlay
 *     bloqueando toda a navegação (deixa logar mas trava a UI).
 *
 * Camada B · Módulo não contratado → rota bloqueada:
 *   - Rotas V360 (`/app/v360/*`) exigem `hasV360`.
 *   - Rotas S360 (dashboard/projetos/sprint-board/gantt/...) exigem
 *     `hasSprints`.
 *   - Se o usuário tenta abrir rota sem o módulo → redirect automático
 *     para o "default seguro" do que ele TEM contratado.
 *
 * Camada C · Preferência do papel (default pós-login):
 *   - Executivo/Adm/Super_adm_empresa/Admin → V360 se contratado, senão S360.
 *   - Demais papéis (consultor/gp/executor) → S360 se contratado, senão V360.
 *   - Se NADA contratado → `/app/subscricoes` (fluxo comercial).
 */
import { Navigate, useLocation } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { useTenant } from "@/context/TenantContext";

// Prefixos de rota associados a cada módulo. Rotas fora destes conjuntos
// são consideradas "neutras" (usuarios, configuracoes, subscricoes, welcome).
const V360_PREFIXES = ["/app/v360"];
const S360_PREFIXES = [
  "/app/dashboard", "/app/projetos", "/app/recomendacoes",
  "/app/sprint-board", "/app/atividades", "/app/gantt",
  "/app/calendario", "/app/documentos", "/app/reunioes",
  "/app/painel-semanal", "/app/sprints360", "/app/s360-empresas",
  "/app/s360/", "/app/analise-ia-s360", "/app/acoes-decisoes",
  "/app/empresas",
];
const NEUTRAL_PREFIXES = [
  "/app/welcome", "/app/usuarios", "/app/configuracoes",
  "/app/subscricoes", "/app/personalizacao", "/app/analise-ia",
];

function matchesAny(pathname, prefixes) {
  return prefixes.some(p => pathname === p || pathname.startsWith(p + "/") || pathname === p.replace(/\/$/, ""));
}

/**
 * Escolhe a rota-default para o usuário com base em plano + papel.
 * @param {string} papel — código do papel (executivo, super_adm_empresa, etc)
 * @param {boolean} hasV360
 * @param {boolean} hasSprints
 * @returns {string} rota absoluta
 */
export function computeDefaultRoute(papel, hasV360, hasSprints) {
  const p = String(papel || "").toLowerCase();
  const prefereV360 = ["executivo", "super_adm_empresa", "adm_empresa", "admin", "super_adm"].includes(p);
  if (prefereV360) {
    if (hasV360)   return "/app/v360/empresa-dashboard";
    if (hasSprints) return "/app/dashboard";
  } else {
    if (hasSprints) return "/app/dashboard";
    if (hasV360)   return "/app/v360/empresa-dashboard";
  }
  // Sem nenhum plano contratado → tela comercial
  return "/app/subscricoes";
}

/**
 * StatusOverlay — Camada A. Bloqueia toda a UI se workspace não está ativo.
 */
function StatusOverlay({ status }) {
  const label = status === "cancelado" ? "cancelado" : "suspenso";
  return (
    <div
      className="fixed inset-0 z-[9999] bg-slate-900/85 backdrop-blur-sm grid place-items-center p-6"
      data-testid="workspace-blocked-overlay"
    >
      <div className="max-w-md w-full rounded-lg border-2 border-red-400 bg-white p-6 shadow-2xl text-center">
        <div className="w-14 h-14 rounded-full bg-red-100 grid place-items-center mx-auto mb-3">
          <AlertTriangle className="w-7 h-7 text-red-600" />
        </div>
        <h1 className="font-display text-xl font-semibold text-slate-900 mb-2">
          Workspace {label}
        </h1>
        <p className="text-sm text-slate-600 leading-snug">
          O acesso a este workspace está temporariamente indisponível. Fale com o seu consultor
          ou o administrador da conta para reativar a assinatura.
        </p>
        <p className="text-[11px] text-slate-400 mt-4">
          Suporte: <a href="mailto:suporte@valora360.com" className="underline">suporte@valora360.com</a>
        </p>
      </div>
    </div>
  );
}

export default function PlanAccessGuard({ children }) {
  const { workspaceStatus, workspaceAtivo, hasV360, hasSprints, currentUser } = useTenant();
  const { pathname } = useLocation();

  // Camada A — bloqueio total por status
  if (currentUser && (workspaceStatus === "suspenso" || workspaceStatus === "cancelado" || workspaceAtivo === false)) {
    return <StatusOverlay status={workspaceStatus} />;
  }

  // Camada B — módulo não contratado
  const papel = currentUser?.papel || "";
  const defaultRoute = computeDefaultRoute(papel, hasV360, hasSprints);

  const isV360Route = matchesAny(pathname, V360_PREFIXES);
  const isS360Route = matchesAny(pathname, S360_PREFIXES);
  const isNeutral   = matchesAny(pathname, NEUTRAL_PREFIXES);

  // Só aplica guard quando temos um usuário autenticado (evita rejeitar
  // durante a hidratação inicial da sessão).
  if (currentUser) {
    if (isV360Route && !hasV360)   return <Navigate to={defaultRoute} replace />;
    if (isS360Route && !hasSprints) return <Navigate to={defaultRoute} replace />;
    // Root /app entrou aqui? redireciona para o default do papel.
    if (pathname === "/app" || (pathname === "/app/dashboard" && !hasSprints && !isNeutral)) {
      return <Navigate to={defaultRoute} replace />;
    }
  }

  return children;
}
